import type { UseQueryResult } from '@tanstack/react-query'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  GET_PRODUCTION_PLAN_OBLIGATION_PERIOD_API,
  getProductionPlanObligationPeriod,
} from '@/features/portfolioHierarchy/endpoints/productionPlanObligationPeriods'
import type { ProductionPlanObligationPeriod } from '@/features/portfolioHierarchy/types'
import type { UseQueryOptions } from '@/types/queries'

export type UseProductionPlanObligationPeriodQueryParams = {
  countryCode: string | null
  regulationObjectCode: string | null
}

export type UseProductionPlanObligationPeriodQueryResult = Omit<UseQueryResult, 'data'> & {
  obligationPeriod: ProductionPlanObligationPeriod | null
}

export function useProductionPlanObligationPeriodQuery(
  params: UseProductionPlanObligationPeriodQueryParams,
  options?: UseQueryOptions<ProductionPlanObligationPeriod>,
): UseProductionPlanObligationPeriodQueryResult {
  const { data = null, ...queryResult } = useQuery<ProductionPlanObligationPeriod>({
    ...options,
    queryFn: () => {
      if (!params.countryCode || !params.regulationObjectCode) {
        throw new Error('Country code and regulation object code are required')
      }
      return getProductionPlanObligationPeriod(params.countryCode, params.regulationObjectCode)
    },
    queryKey: [
      GET_PRODUCTION_PLAN_OBLIGATION_PERIOD_API,
      { countryCode: params.countryCode, regulationObjectCode: params.regulationObjectCode },
    ],
    enabled: !!params.countryCode && !!params.regulationObjectCode,
  })

  return useMemo(
    () => ({
      ...queryResult,
      obligationPeriod: data,
    }),
    [data, queryResult],
  )
}

export function useInvalidateProductionPlanObligationPeriodQuery() {
  const queryClient = useQueryClient()

  return (countryCode: string, regulationObjectCode: string) =>
    queryClient.invalidateQueries({
      queryKey: [GET_PRODUCTION_PLAN_OBLIGATION_PERIOD_API, { countryCode, regulationObjectCode }],
    })
}
